import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Card, Badge } from "@/components/primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Radio } from "lucide-react";

export default function Events() {
  const [data, setData] = useState({ items: [], total: 0 });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", payload: "{}", entity_type: "", entity_id: "" });

  const load = async () => {
    const r = await api.get("/events?limit=200");
    setData(r.data);
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, []);

  const emit = async () => {
    try {
      let payload;
      try { payload = JSON.parse(form.payload || "{}"); } catch { return toast.error("Invalid JSON payload"); }
      await api.post("/events", { name: form.name, payload, entity_type: form.entity_type || null, entity_id: form.entity_id || null });
      toast.success("Event emitted");
      setOpen(false);
      setForm({ name: "", payload: "{}", entity_type: "", entity_id: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="events-page">
      <PageHeader
        title="Events Stream"
        subtitle="Event Engine"
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-hocp-mint text-hocp-deepTeal hover:bg-hocp-mintStrong font-semibold h-9 px-4 text-[13px]" data-testid="emit-event-button"><Plus className="w-3.5 h-3.5 mr-1.5"/> Emit event</Button>
            </DialogTrigger>
            <DialogContent className="bg-hocp-bgSubtle border-hocp-border">
              <DialogHeader><DialogTitle className="font-heading">Publish event</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label className="text-xs uppercase tracking-wider text-hocp-textMuted">Event name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="UserCreated" className="bg-hocp-bgCard border-hocp-border mt-1.5 mono" data-testid="event-name-input"/></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs uppercase tracking-wider text-hocp-textMuted">Entity type</Label><Input value={form.entity_type} onChange={(e) => setForm({ ...form, entity_type: e.target.value })} className="bg-hocp-bgCard border-hocp-border mt-1.5 mono" data-testid="event-entity-type-input"/></div>
                  <div><Label className="text-xs uppercase tracking-wider text-hocp-textMuted">Entity ID</Label><Input value={form.entity_id} onChange={(e) => setForm({ ...form, entity_id: e.target.value })} className="bg-hocp-bgCard border-hocp-border mt-1.5 mono" data-testid="event-entity-id-input"/></div>
                </div>
                <div><Label className="text-xs uppercase tracking-wider text-hocp-textMuted">Payload (JSON)</Label>
                  <Textarea value={form.payload} onChange={(e) => setForm({ ...form, payload: e.target.value })} rows={6} className="bg-hocp-bgCard border-hocp-border mt-1.5 mono text-xs" data-testid="event-payload-input"/>
                </div>
              </div>
              <DialogFooter><Button onClick={emit} className="bg-hocp-mint text-hocp-deepTeal hover:bg-hocp-mintStrong font-semibold" data-testid="submit-event-button">Publish</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />
      <Card className="p-0" data-testid="events-list">
        <div className="px-5 py-3 border-b border-hocp-border flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Radio className="w-3.5 h-3.5 text-emerald-500 animate-pulse" strokeWidth={1.5}/>
            <span className="text-[11px] uppercase tracking-[0.15em] text-hocp-textMuted mono">Live stream</span>
          </div>
          <Badge tone="neutral">{data.total} total</Badge>
        </div>
        <div className="max-h-[70vh] overflow-y-auto">
          {data.items.length === 0 && <div className="p-10 text-center text-xs text-hocp-textMuted">No events yet.</div>}
          {data.items.map((e) => (
            <div key={e.id} className="px-5 py-3 border-b border-hocp-border last:border-0 hover:bg-hocp-bgSubtle/50 transition-colors">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <Badge tone="info">{e.name}</Badge>
                  {e.entity_type && <span className="mono text-[11px] text-hocp-textMuted">{e.entity_type} · {String(e.entity_id || "").slice(-8)}</span>}
                </div>
                <span className="mono text-[10px] text-hocp-textTertiary shrink-0">{new Date(e.timestamp).toLocaleString()}</span>
              </div>
              {Object.keys(e.payload || {}).length > 0 && (
                <pre className="mono text-[11px] text-hocp-textMuted mt-2 bg-hocp-bgCard border border-hocp-border rounded px-3 py-2 overflow-x-auto">{JSON.stringify(e.payload, null, 2)}</pre>
              )}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
